import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  mergeMap,
  withLatestFrom,
  tap,
  map,
} from 'rxjs/operators';
import { LicenseService } from '@core/services';
import { Licencia, LocalForm } from '@core/models';
import { selectStoreForm } from '@store/selectors';
import {
  LICENSE_CREATE_VIEW,
  LICENSE_EDIT_MODE,
  LICENSE_FIND_ALL,
  LICENSE_FIND_ALL_SUCCESS,
  LICENSE_UPDATE_LOADING,
  LICENSE_FAILURE,
  LICENSE_UPDATE_VIEW,
  LICENSE_UPDATE_ID,
  LICENSE_UPDATE,
  LICENSE_UPDATE_COMPLETED,
  LICENSE_FIND,
  LICENSE_FIND_SUCCESS,
  NOTIFICATION_SHOW,
  SERVICE_FAILURE,
} from '@store/actions';

@Injectable()
export class LicenseEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private licenseService: LicenseService
  ) {}

  createView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LICENSE_CREATE_VIEW),
      mergeMap(() => [
        LICENSE_EDIT_MODE({ editMode: true, isEditing: false }),
      ])
    )
  );

  updateView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LICENSE_UPDATE_VIEW),
      mergeMap(({ licenseId }) => [
        LICENSE_UPDATE_ID({ licenseId }),
        LICENSE_EDIT_MODE({ editMode: true, isEditing: true }),
      ])
    )
  );

  find$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LICENSE_FIND),
      mergeMap(({ id }) => {
        this.store.dispatch(LICENSE_UPDATE_LOADING({ loading: true }));
        return this.licenseService.find(id).pipe(
          mergeMap(({ data }) => {
            const [license] = data as Licencia[];
            return [
              LICENSE_FIND_SUCCESS({ license }),
              LICENSE_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              LICENSE_FAILURE({ error }),
              LICENSE_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LICENSE_FIND_ALL),
      mergeMap((filters) => {
        this.store.dispatch(LICENSE_UPDATE_LOADING({ loading: true }));
        return this.licenseService.findAll(filters).pipe(
          mergeMap(({ data }) => {
            const licenseList = data as Licencia[];
            return [
              LICENSE_FIND_ALL_SUCCESS({ licenseList }),
              LICENSE_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              LICENSE_FAILURE({ error }),
              LICENSE_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  upsert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LICENSE_UPDATE),
      withLatestFrom(
        this.store.select(selectStoreForm),
      ),
      filter(([_, storeForm]) => !!storeForm),
      map(([_, storeForm]) => ({ storeForm: storeForm as LocalForm })),
      mergeMap(({ storeForm }) => {
        this.store.dispatch(LICENSE_UPDATE_LOADING({ loading: true }));
        return this.licenseService.upsert(storeForm).pipe(
          mergeMap(() => [
            NOTIFICATION_SHOW({
              notification: {
                type: 'success',
                title: 'Success',
                detail: 'License updated successfully',
              },
            }),
            LICENSE_UPDATE_COMPLETED(),
            LICENSE_UPDATE_LOADING({ loading: false }),
            LICENSE_UPDATE_COMPLETED(),
          ]),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              LICENSE_FAILURE({ error }),
              LICENSE_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  upsertComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LICENSE_UPDATE_COMPLETED),
        tap(() => {
          this.licenseService.upsertCompleted();
        })
      ),
    { dispatch: false }
  );
}
